import { useApp } from "../../../hooks/useApp"
import { MainProductVariant, SinglePendantProductVariant } from "../../../types"
import { getMainProductVariantById, getPendantProductVariantById, getSinglePendantMainProductVariant, getSingleProductVariantFromMainProductByLengthInTitle } from "../../../utils"
import { Text } from "../../ui/Text/Text"
import { formatReadyWord, getProductionDate } from "./utils"

interface Props {
  className?: string
}

const Label = ({ className, label, disclaimer }: { className?: string, label?: string, disclaimer?: string }) => {
  return (
    <div className={className}>
      {label && (
        <p>
          <Text 
            className="font-calibre text-primary"
            style="label"
          >
            {label}
          </Text>
        </p>
      )}

      {disclaimer && (
        <p className={label ? 'mt-1' : undefined}>
          <Text className="text-primary/70" style="caption">
            {disclaimer}
          </Text>
        </p>
      )}
    </div>
  )
}

export const DispatchEstimate = ({ className }: Props) => {
  const {
    mainProduct,
    active,
    activePendantProductVariantIds,
    activeMainProductVariantId,
    previewPendantProductVariantId,
    isMainProductSinglePendant,
    isCarabinerProductMode
  } = useApp()
  const productionDate = getProductionDate(mainProduct)
  const estimatedDispatchLabel = productionDate
    ? `Estimated dispatch on ${productionDate}`
    : formatReadyWord()

  const previewPendantProductVariant = !isMainProductSinglePendant && previewPendantProductVariantId
    ? getPendantProductVariantById(mainProduct, previewPendantProductVariantId)
    : undefined

  if (previewPendantProductVariant && !previewPendantProductVariant.available) {
    return <Label className={className} label={previewPendantProductVariant.metafields.dispatchDisclaimer} />
  }

  let mainProductVariant: MainProductVariant | SinglePendantProductVariant | undefined
  let dispatchDisclaimer: string | undefined

  switch (true) {
    case isCarabinerProductMode:
      mainProductVariant = getMainProductVariantById(mainProduct, activeMainProductVariantId)
      break
    case isMainProductSinglePendant:
      mainProductVariant = getSinglePendantMainProductVariant(mainProduct, +activeMainProductVariantId, +activePendantProductVariantIds[0])
      break
    case activePendantProductVariantIds.length === 1:
      mainProductVariant = getSingleProductVariantFromMainProductByLengthInTitle(mainProduct, +activeMainProductVariantId, +activePendantProductVariantIds[0])
      break
    default: {
      const activePendantProductVariantId = activePendantProductVariantIds[active] ?? activePendantProductVariantIds[activePendantProductVariantIds.length - 1]
      const activePendantProductVariant = activePendantProductVariantId
        ? getPendantProductVariantById(mainProduct, activePendantProductVariantId)
        : undefined

      return (
        <Label
          className={className}
          label={estimatedDispatchLabel}
          disclaimer={activePendantProductVariant?.metafields.dispatchDisclaimer}
        />
      )
    }
  }

  if (!mainProductVariant) {
    console.error('Product variant not found to calculate dispatch time', mainProduct, activeMainProductVariantId)
    return <Label className={className} label={estimatedDispatchLabel} />
  }

  if (!mainProductVariant.available) {
    return null
  }

  dispatchDisclaimer = previewPendantProductVariant?.metafields.dispatchDisclaimer || mainProductVariant.metafields.dispatchDisclaimer

  if (mainProductVariant.inventoryQuantity > 0) {
    return <Label className={className} label={formatReadyWord()} disclaimer={dispatchDisclaimer} />
  }

  return <Label className={className} label={estimatedDispatchLabel} disclaimer={dispatchDisclaimer} />
}